import React, { useEffect, useMemo } from 'react';
import { useFetchSelectedLevelOptions } from '@retail/calculus/context';
import { SelectChangeEvent, Stack } from '@mui/material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useController, useFormContext } from 'react-hook-form';
import {
  assortmentLevels,
  LevelSelectForm,
  LevelSelectSchema,
  supplierLevels,
} from '@retail/calculus/components';
import { itemPackageLevelValue, SelectPackageLevel } from '@retail/markup/components';
import { getClassCodeLabel } from '@retail/products/utils';
import { useDisclosure } from '@shared/hooks';

interface Props {
  supplierNumber?: number;
  includePackages?: boolean;
  excistingConditionLevelIds?: string[];
  children: (
    selectedLevelResult: ReturnType<typeof useFetchSelectedLevelOptions>
  ) => React.ReactNode;
}

export const ConditionLevelSelectContainer: React.FC<Props> = ({
  supplierNumber,
  includePackages,
  excistingConditionLevelIds,
  children,
}) => {
  const t = useCalculusTFunction();
  const { appTenant } = useSelectedOrgUnit();

  const { watch, control, setError, resetField, clearErrors } = useFormContext<LevelSelectSchema>();

  const level = watch('level');
  const overGroupValue = watch('overGroup');
  const discountGroupsValue = watch('discountGroups');
  const itemValue = watch('item');
  const levelController = useController({ name: 'level', control });
  const itemController = useController({ name: 'item', control });
  const gtinController = useController({ name: 'gtin' });
  const itemSearch = watch('itemSearch');

  const excludeWithExcistingConditions = useDisclosure();

  useEffect(() => {
    clearErrors('item');
  }, [clearErrors, itemSearch]);

  const selectedLevelResult = useFetchSelectedLevelOptions({
    supplierNumber,
    selectedProductGroup: level === 'productGroup',
    selectedDiscountGroup: level === 'discountGroup',
    itemSearch,
    selectedMgItemNumber: itemController.field.value?.mgItemNumber,
    onItemSearchSuccess: (results) => {
      if (!results.result.length) {
        setError('item', { message: t('calculus.error.itemSearch.noResults') });
      }
    },
    onItemSearchError: (err) => {
      console.error(err);
      setError('item', { message: t('calculus.error.itemSearch.generic') });
    },
  });

  const packages = useMemo(() => {
    const supplierItem = selectedLevelResult.mgItem?.mgSupplierItems.find(
      ({ mgSupplier }) => mgSupplier.mgSupplierId === supplierNumber
    );
    return supplierItem?.getSalesPackagesOrdered().filter(({ type }) => type !== 'PRIMARY');
  }, [selectedLevelResult.mgItem, supplierNumber]);

  const packageOptions = useMemo(() => {
    if (!packages) return undefined;

    const itemLevelOption = {
      value: itemPackageLevelValue,
      name: t('calculus.level.mgItemLevel'),
    };

    return [
      itemLevelOption,
      ...packages.map(({ type, gtinCode, unit }) => ({
        value: gtinCode,
        name: getClassCodeLabel(type || ''),
        description: unit.unit,
      })),
    ];
  }, [packages, t]);

  const hasMultiplePackages = packageOptions && packageOptions.length > 1;

  useEffect(() => {
    if (!hasMultiplePackages && !!includePackages && itemController.field.value) {
      gtinController.field.onChange('ITEM');
    }
  }, [gtinController.field, hasMultiplePackages, includePackages, itemController.field.value]);

  const handleLevelSelectChange = (value: SelectChangeEvent<unknown>) => {
    resetField('item');
    resetField('overGroup');
    resetField('mainGroup');
    resetField('productGroup');
    resetField('discountGroups');
    levelController.field.onChange(value);
  };

  const showChildren = useMemo(() => {
    if (!selectedLevelResult) return false;
    if (level === 'assortment' || level === 'supplier') return true;
    if (level === 'productGroup') {
      return !!overGroupValue;
    }
    if (level === 'discountGroup') {
      return discountGroupsValue?.length > 0;
    }
    if (level === 'item') {
      return !!itemValue;
    }
    return false;
  }, [selectedLevelResult, level, overGroupValue, discountGroupsValue, itemValue]);

  const discountGroups = useMemo(
    () =>
      excludeWithExcistingConditions.isOpen && excistingConditionLevelIds
        ? selectedLevelResult.discountGroups.filter(
            (discountGroup) => !excistingConditionLevelIds.includes(discountGroup.code)
          )
        : selectedLevelResult.discountGroups,
    [
      excistingConditionLevelIds,
      excludeWithExcistingConditions.isOpen,
      selectedLevelResult.discountGroups,
    ]
  );

  const productGroups = useMemo(
    () =>
      excludeWithExcistingConditions.isOpen && excistingConditionLevelIds
        ? selectedLevelResult.productGroups.filter(
            (productGroup) => !excistingConditionLevelIds.includes(productGroup.groupNumber)
          )
        : selectedLevelResult.productGroups,
    [
      excistingConditionLevelIds,
      excludeWithExcistingConditions.isOpen,
      selectedLevelResult.productGroups,
    ]
  );
  return (
    <>
      <Stack pb={2}>
        <LevelSelectForm
          levels={supplierNumber ? supplierLevels : assortmentLevels}
          discountGroups={discountGroups}
          productGroups={productGroups}
          mgSupplierItems={selectedLevelResult.mgSupplierItems}
          isLoadingFetchItems={selectedLevelResult.isLoadingFetchItems}
          externalItemSource={appTenant.externalItemSource}
          excludeWithExcistingConditions={
            excistingConditionLevelIds ? excludeWithExcistingConditions : undefined
          }
          handleLevelSelectChange={handleLevelSelectChange}
        />
        {includePackages && hasMultiplePackages && itemController.field.value && (
          <SelectPackageLevel gtinController={gtinController} packageOptions={packageOptions} />
        )}
      </Stack>
      {showChildren && children(selectedLevelResult)}
    </>
  );
};
